import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Input, Switch } from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { LookupItem, LookupCreateUpdatePayload } from '../api/lookupApi'

type Form = {
  code: string
  name: string
  description: string
  color: string
  sortOrder: number
  isActive: boolean
}

type Props = {
  api: {
    list: (params: any) => Promise<any>
    create: (payload: LookupCreateUpdatePayload) => Promise<any>
    update: (id: number, payload: LookupCreateUpdatePayload) => Promise<any>
    delete: (id: number) => Promise<any>
  }
  i18nPrefix: string
  showColor?: boolean
}

export default function RefDataCrudPage({ api, i18nPrefix, showColor = false }: Props) {
  const { t } = useTranslation()

  return (
    <CrudPage<LookupItem, Form, LookupCreateUpdatePayload, LookupCreateUpdatePayload>
      title={t(`${i18nPrefix}.title`)}
      subtitle={t(`${i18nPrefix}.subtitle`)}
      primaryActionLabel={t(`${i18nPrefix}.newItem`)}
      list={async (p) =>
        api.list({
          search: p.search || undefined,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: (p.sortBy as 'code' | 'name') ?? undefined,
          sortDir: (p.sortDir as 'asc' | 'desc') ?? undefined,
        })
      }
      getRowId={(o) => o.id}
      columns={[
        {
          key: 'code',
          header: t(`${i18nPrefix}.columns.code`),
          sortable: true,
          widthClassName: 'w-[160px]',
          render: (o) => <span className="font-medium">{o.code}</span>,
        },
        {
          key: 'name',
          header: t(`${i18nPrefix}.columns.name`),
          sortable: true,
          widthClassName: 'w-[300px]',
          render: (o) => o.name,
        },
        {
          key: 'isActive',
          header: t(`${i18nPrefix}.columns.isActive`),
          sortable: false,
          widthClassName: 'w-[100px]',
          render: (o) =>
            o.isActive ? (
              <Badge variant="success">{t(`${i18nPrefix}.status.active`)}</Badge>
            ) : (
              <Badge variant="warning">{t(`${i18nPrefix}.status.inactive`)}</Badge>
            ),
        },
        {
          key: 'sortOrder',
          header: t(`${i18nPrefix}.columns.sortOrder`),
          sortable: false,
          widthClassName: 'w-[80px]',
          render: (o) => o.sortOrder,
        },
      ]}
      searchPlaceholder={t(`${i18nPrefix}.searchPlaceholder`)}
      initialPageSize={10}
      modalTitles={{
        create: t(`${i18nPrefix}.modal.create`),
        edit: t(`${i18nPrefix}.modal.edit`),
      }}
      getCreateDefaultForm={() => ({
        code: '',
        name: '',
        description: '',
        color: '',
        sortOrder: 0,
        isActive: true,
      })}
      getEditForm={(o) => ({
        code: o.code,
        name: o.name,
        description: o.description ?? '',
        color: o.color ?? '',
        sortOrder: o.sortOrder,
        isActive: o.isActive,
      })}
      validate={(f) => ({
        code: !f.code.trim() ? t(`${i18nPrefix}.validation.codeRequired`) : undefined,
        name: !f.name.trim() ? t(`${i18nPrefix}.validation.nameRequired`) : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <Input
            label={t(`${i18nPrefix}.fields.code`)}
            value={form.code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, code: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.code}
            helperText={touched ? errors.code : undefined}
            required
            disabled={isSubmitting}
            placeholder={t(`${i18nPrefix}.fields.codePlaceholder`)}
            leftIcon={<i className="material-symbols-outlined text-lg">tag</i>}
          />
          <Input
            label={t(`${i18nPrefix}.fields.name`)}
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, name: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            required
            disabled={isSubmitting}
            placeholder={t(`${i18nPrefix}.fields.namePlaceholder`)}
            leftIcon={<i className="material-symbols-outlined text-lg">label</i>}
          />
          <Input
            label={t(`${i18nPrefix}.fields.description`)}
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, description: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder={t(`${i18nPrefix}.fields.descriptionPlaceholder`)}
            leftIcon={<i className="material-symbols-outlined text-lg">description</i>}
          />
          {showColor && (
            <Input
              label={t(`${i18nPrefix}.fields.color`)}
              value={form.color}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((s) => ({ ...s, color: e.target.value }))
              }
              disabled={isSubmitting}
              placeholder="#FF0000"
              leftIcon={<i className="material-symbols-outlined text-lg">palette</i>}
            />
          )}
          <Input
            label={t(`${i18nPrefix}.fields.sortOrder`)}
            type="number"
            value={String(form.sortOrder)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, sortOrder: parseInt(e.target.value, 10) || 0 }))
            }
            disabled={isSubmitting}
            placeholder="0"
          />
          <Switch
            checked={form.isActive}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isActive: v }))}
            label={t(`${i18nPrefix}.fields.isActive`)}
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        code: f.code.trim(),
        name: f.name.trim(),
        description: f.description.trim() || null,
        color: f.color.trim() || null,
        sortOrder: f.sortOrder,
        isActive: f.isActive,
      })}
      buildUpdatePayload={(f) => ({
        code: f.code.trim(),
        name: f.name.trim(),
        description: f.description.trim() || null,
        color: f.color.trim() || null,
        sortOrder: f.sortOrder,
        isActive: f.isActive,
      })}
      create={(payload) => api.create(payload)}
      update={(id, payload) => api.update(Number(id), payload)}
      remove={(id) => api.delete(Number(id))}
      getCreatedId={(res) => res?.id}
    />
  )
}
